import { SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Input, Row, Space, Typography } from 'antd';
import { useCallback, useContext, useMemo, useState } from 'react';

import { Avatar } from '@app/components/Avatar';
import { PREFIX } from '@app/constants';
import { useContentWidth, useWindowSize } from '@app/hooks';
import {
  CHF_EMPLOYEE_IDS,
  CHF_VISITS_FILTER,
} from '@app/pages/Clients/ClientsList/constants/filterKeysAndIds';
import { ClientFiltersContext } from '@app/providers';
import type { NClients } from '@app/types';
import type { NEmployees } from '@app/types/Employees';

import { hideFilterBlock } from '@pages/Clients/ClientsList/utils';

import type { CheckboxChangeEvent } from 'antd/es/checkbox';

import '@pages/Clients/ClientsList/styles/Filters.desktop.scss';

const { Text } = Typography;

const filterEmployeesByQuery = (
  employeesFilteredByActivity: NClients.ModelClientsFilterData['Employees'],
  query: string
): NClients.ModelClientsFilterData['Employees'] => {
  const filteredEmployees = employeesFilteredByActivity.filter(
    ({ NameFirst, NameLast }) => {
      const handledQuery = query.trim().toLowerCase();

      const handledFirstName = NameFirst.toLowerCase();
      const handledLastName = NameLast.toLowerCase();

      const isFirstNameContainsQuery = handledFirstName.includes(handledQuery);
      const isLastNameContainsQuery = handledLastName.includes(handledQuery);

      if (!query || isFirstNameContainsQuery || isLastNameContainsQuery) {
        return true;
      }

      return false;
    }
  );

  return filteredEmployees;
};

export const VisitsEmployees = ({
  filterGroupIndex,
}: {
  filterGroupIndex: number;
}) => {
  const {
    filters,
    allEmployees,
    handleDisabling,
    setUpServerDataChunk,
    onChangeFiltersHandler,
  } = useContext(ClientFiltersContext);
  const { innerWidth } = useWindowSize();
  const { contentWidth } = useContentWidth();

  const [query, setQuery] = useState('');

  const defineEmployeeIdsList = useCallback(
    (
      value: NEmployees.Employee['Id']
    ): Array<NEmployees.Employee['Id']> | null => {
      const filteredData =
        filters?.VisitsFilter?.[
          filterGroupIndex
        ]?.Values?.[0]?.EmployeeIds?.filter((Id) => Id !== value) || [];

      return filteredData.length > 0 ? filteredData : null;
    },
    [filters, filterGroupIndex]
  );

  const onChange = useCallback(
    (event: CheckboxChangeEvent) => {
      const { target } = event;
      const { checked, value } = target;

      const EmployeeIds = checked
        ? [
            ...(filters?.VisitsFilter?.[filterGroupIndex]?.Values?.[0]
              ?.EmployeeIds || []),
            value as NEmployees.Employee['Id'],
          ]
        : defineEmployeeIdsList(value);

      onChangeFiltersHandler(
        'VisitsFilter',
        filterGroupIndex,
        'EmployeeIds',
        EmployeeIds
      );
    },
    [filters, filterGroupIndex, defineEmployeeIdsList, onChangeFiltersHandler]
  );

  const activeEmployees = useMemo(
    () => allEmployees.filter(({ IsDeleted }) => !IsDeleted),
    [allEmployees]
  );

  const deletedEmployees = useMemo(
    () => allEmployees.filter(({ IsDeleted }) => IsDeleted),
    [allEmployees]
  );

  const memoizedListStyles = useMemo(() => {
    let width = '';

    if (innerWidth > 950) {
      width = 'auto';
    } else if (792 < innerWidth && innerWidth <= 950) {
      width = `${contentWidth / 2 - 32}px`;
    } else if (550 < innerWidth && innerWidth <= 792) {
      width = `${contentWidth - 32}px`;
    } else {
      width = `${contentWidth}px`;
    }

    return {
      width,
    };
  }, [contentWidth, innerWidth]);

  const activeEmployeesFilteredByQuery = useMemo(
    () => filterEmployeesByQuery(activeEmployees, query),
    [activeEmployees, query]
  );

  const deletedEmployeesFilteredByQuery = useMemo(
    () => filterEmployeesByQuery(deletedEmployees, query),
    [deletedEmployees, query]
  );

  return (
    <Space direction="vertical" onClick={(e) => e.stopPropagation()}>
      <Input
        placeholder="Поиск по фамилиям и именам"
        addonBefore={<SearchOutlined />}
        value={query}
        onChange={({ target }) => setQuery(target.value)}
      />

      <Space direction="vertical">
        <Text strong>Активные</Text>
        <ul
          className={`${PREFIX}client-filters-employees-list`}
          style={memoizedListStyles}
        >
          {activeEmployeesFilteredByQuery.length > 0 ? (
            activeEmployeesFilteredByQuery.map(
              ({ Id, NameFirst, NameLast, Photo }) => {
                const fullName = `${
                  NameLast ? `${NameLast} ` : ''
                }${NameFirst}`;
                const isChecked = Boolean(
                  filters.VisitsFilter?.[
                    filterGroupIndex
                  ]?.Values?.[0]?.EmployeeIds?.find((id) => id === Id)
                );

                return (
                  <li key={Id}>
                    <Checkbox
                      value={Id}
                      checked={isChecked}
                      onChange={onChange}
                    >
                      <Space>
                        <Avatar
                          src={Photo}
                          nameFirst={NameFirst}
                          nameLast={NameLast}
                          size={32}
                        />
                        <Text>{fullName}</Text>
                      </Space>
                    </Checkbox>
                  </li>
                );
              }
            )
          ) : (
            <span>Ничего не найдено.</span>
          )}
        </ul>
      </Space>

      <Space direction="vertical" style={{ width: '100%' }}>
        <Text strong>Уволенные</Text>

        <ul
          className={`${PREFIX}client-filters-employees-list`}
          style={memoizedListStyles}
        >
          {deletedEmployeesFilteredByQuery.length > 0 ? (
            deletedEmployeesFilteredByQuery.map(
              ({ Id, NameFirst, NameLast, Photo }) => {
                const fullName = `${
                  NameLast ? `${NameLast} ` : ''
                }${NameFirst}`;
                const isChecked = Boolean(
                  filters.VisitsFilter?.[
                    filterGroupIndex
                  ]?.Values?.[0]?.EmployeeIds?.find((id) => id === Id)
                );

                return (
                  <li key={Id}>
                    <Checkbox
                      value={Id}
                      checked={isChecked}
                      onChange={onChange}
                    >
                      <Space>
                        <Avatar
                          src={Photo}
                          nameFirst={NameFirst}
                          nameLast={NameLast}
                          size={32}
                        />
                        <Text>{fullName}</Text>
                      </Space>
                    </Checkbox>
                  </li>
                );
              }
            )
          ) : (
            <span>Ничего не найдено.</span>
          )}
        </ul>
      </Space>

      <Divider style={{ marginTop: '8px', marginBottom: '12px' }} />
      <Row justify="end">
        <Button
          type="primary"
          size="small"
          disabled={handleDisabling(
            CHF_VISITS_FILTER,
            CHF_EMPLOYEE_IDS,
            false,
            '',
            filterGroupIndex
          )}
          onClick={() => {
            setUpServerDataChunk(
              CHF_VISITS_FILTER,
              CHF_EMPLOYEE_IDS,
              false,
              '',
              filterGroupIndex
            );

            hideFilterBlock(
              '.visits-employees-label',
              innerWidth,
              filterGroupIndex
            );
          }}
        >
          Выбрать
        </Button>
      </Row>
    </Space>
  );
};
